/**
 * Route metadata lookup for the browser (Release 2.0.1, task R02).
 *
 * `data/routeMeta.json` is the one reviewed source of per-route title,
 * description, and canonical identity. This module is the React side of it;
 * `scripts/generate-target-artifacts.js` is the build side, and reads the same
 * file. Neither owns a metadata string, so the raw HTML a crawler receives and
 * the head React produces after hydration cannot drift apart —
 * `__tests__/routeMetaContract.test.js` asserts that byte for byte.
 *
 * Routes are keyed by their canonical PARENT path. A page served on an app
 * subdomain shares the entry of its /apps/<brand>/... twin, which is what makes
 * the D-02 canonical rule hold on both mounts (see utils/brandRouting.js).
 */
import routeMeta from '../data/routeMeta.json';

export const { parentOrigin: PARENT_ORIGIN, siteName: SITE_NAME, titleSuffix: TITLE_SUFFIX } = routeMeta;
export const DEFAULT_TITLE = routeMeta.defaultTitle;
export const DEFAULT_DESCRIPTION = routeMeta.defaultDescription;

export const ROUTE_META = routeMeta.meta;
export const TARGETS = routeMeta.targets;

/** Absolute canonical URL for a parent-site path ("/" keeps its slash). */
export function canonicalUrlFor(canonicalPath) {
  return canonicalPath === '/' ? `${PARENT_ORIGIN}/` : `${PARENT_ORIGIN}${canonicalPath}`;
}

/**
 * The `<title>` a page actually renders. Kept here rather than in the hook so
 * the generator can apply the identical rule from the same JSON.
 */
export function documentTitleFor(title) {
  return title ? `${title}${TITLE_SUFFIX}` : DEFAULT_TITLE;
}

/**
 * Props for useDocumentMeta. Pages pass only their canonical path, so a page
 * component can no longer hold a metadata string of its own.
 */
export default function routeMetaFor(canonicalPath) {
  const entry = ROUTE_META[canonicalPath];
  if (!entry) throw new Error(`no route metadata for ${canonicalPath}`);
  return {
    title: entry.title,
    description: entry.description,
    canonicalUrl: canonicalUrlFor(canonicalPath),
  };
}
